"use client"

import { useTranslations } from "next-intl"
import { createPortal } from "react-dom"
import { useEffect, useMemo, useState } from "react"
import { motion } from "framer-motion"
import { Plus } from "lucide-react"
import { cn } from "@/lib/utils"
import { CHAT_QUICK_REACTION_EMOJIS } from "@/lib/chat/message-reaction"
import {
  computeOverlayStack,
  reactionBarWidth,
  REACTION_BTN_SIZE,
} from "@/lib/chat/overlay-stack-layout"
import { AnchoredMenuPanel, type AnchoredMenuItem } from "@/components/ui/anchored-menu-panel"
import { dropdownPanelClass } from "@/lib/dropdown-styles"
import { isCopyableMessage } from "@/lib/chat/reply-preview"
import type { MessageAnchorRect } from "@/hooks/use-chat-message-interactions"
import type { ChatMessage } from "@/lib/chat/map-api-message"
import type { ChatReplyPreview } from "@/lib/chat/reply-preview"
import type { ChatMessageReactionCounts } from "@/lib/chat/message-reactions-ui"
import { ChatMessageItem } from "@/components/chat-message"
import { ChatEmojiPickerSheet } from "@/components/chat/ChatEmojiPickerSheet"

export type ChatMessageOverlayProps = {
  visible: boolean
  message: ChatMessage
  anchor: MessageAnchorRect
  isOwnMessage: boolean
  displayName: string
  avatarUrl?: string | null
  showAvatar: boolean
  showTail: boolean
  replyPreview?: ChatReplyPreview | null
  reactions?: ChatMessageReactionCounts
  viewerReaction?: string | null
  canReport: boolean
  roomName?: string
  currentUserId?: string | null
  onDismiss: () => void
  onReply: () => void
  onReport: () => void
  onCopy: () => void
  onReaction: (emoji: string) => void
}

const STACK_MOTION = { type: "spring" as const, stiffness: 420, damping: 34, mass: 0.85 }

export function ChatMessageOverlay({
  visible,
  message,
  anchor,
  isOwnMessage,
  displayName,
  avatarUrl,
  showAvatar,
  showTail,
  replyPreview = null,
  reactions,
  viewerReaction = null,
  canReport,
  roomName,
  currentUserId,
  onDismiss,
  onReply,
  onReport,
  onCopy,
  onReaction,
}: ChatMessageOverlayProps) {
  const tChat = useTranslations("chat")
  const [mounted, setMounted] = useState(false)
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false)
  const [screenH, setScreenH] = useState(800)
  const canCopy = isCopyableMessage(message)

  useEffect(() => setMounted(true), [])

  useEffect(() => {
    if (!visible) setEmojiPickerOpen(false)
  }, [visible])

  useEffect(() => {
    const sync = () => setScreenH(window.innerHeight)
    sync()
    window.addEventListener("resize", sync)
    return () => window.removeEventListener("resize", sync)
  }, [])

  const menuItems = useMemo((): AnchoredMenuItem[] => {
    const items: AnchoredMenuItem[] = [{ id: "reply", title: tChat("reply") }]
    if (canCopy) items.push({ id: "copy", title: tChat("copy") })
    if (canReport) items.push({ id: "report", title: tChat("reportMessage"), destructive: true })
    return items
  }, [canCopy, canReport, tChat])

  const layout = useMemo(
    () => computeOverlayStack(anchor, menuItems.length, screenH),
    [anchor, menuItems.length, screenH],
  )

  // +1 for the trailing "more reactions" (+) button.
  const reactionBarW = reactionBarWidth(CHAT_QUICK_REACTION_EMOJIS.length + 1)

  const reactionLeft =
    typeof window !== "undefined"
      ? Math.max(
          12,
          Math.min(
            anchor.x + anchor.width / 2 - reactionBarW / 2,
            window.innerWidth - reactionBarW - 12,
          ),
        )
      : anchor.x

  const initialReactionTop = anchor.y - 52 - 12

  const handleMenuSelect = (id: string) => {
    if (id === "reply") onReply()
    else if (id === "copy") onCopy()
    else if (id === "report") onReport()
  }

  const handleEmojiSelect = (emoji: string) => {
    setEmojiPickerOpen(false)
    onReaction(emoji)
  }

  if (!mounted || !visible) return null

  return createPortal(
    <div className="fixed inset-0 z-[10040]" role="presentation">
      {!emojiPickerOpen ? (
        <>
          <button
            type="button"
            className="absolute inset-0 z-[1] bg-black/40 backdrop-blur-sm"
            aria-label={tChat("close")}
            onClick={onDismiss}
          />
          <motion.div
            className={cn(
              "absolute z-[3] flex items-center justify-center gap-0.5 overflow-visible rounded-full border px-1.5 py-1.5 shadow-2xl",
              dropdownPanelClass("panel"),
            )}
            style={{ left: reactionLeft, width: reactionBarW }}
            initial={{ top: initialReactionTop }}
            animate={{ top: layout.reactionTop }}
            transition={STACK_MOTION}
          >
            {CHAT_QUICK_REACTION_EMOJIS.map((emoji) => {
              const selected = viewerReaction === emoji
              return (
                <button
                  key={emoji}
                  type="button"
                  className={cn(
                    "box-border flex shrink-0 items-center justify-center rounded-full p-0 text-xl leading-none transition-colors active:scale-95 active:origin-center",
                    selected
                      ? "bg-white/15 ring-2 ring-white/30"
                      : "hover:bg-neutral-700 active:bg-neutral-600",
                  )}
                  style={{ width: REACTION_BTN_SIZE, height: REACTION_BTN_SIZE }}
                  aria-label={tChat("reactA11y", { emoji })}
                  aria-pressed={selected}
                  onClick={() => onReaction(emoji)}
                >
                  {emoji}
                </button>
              )
            })}
            <button
              type="button"
              className="box-border flex shrink-0 items-center justify-center rounded-full border border-neutral-600 p-0 text-neutral-400 transition-colors hover:bg-neutral-700 active:scale-95 active:origin-center active:bg-neutral-600"
              style={{ width: REACTION_BTN_SIZE, height: REACTION_BTN_SIZE }}
              aria-label={tChat("moreReactionsA11y")}
              onClick={() => setEmojiPickerOpen(true)}
            >
              <Plus className="h-4 w-4" />
            </button>
          </motion.div>
          <motion.div
            className="pointer-events-none absolute z-[2]"
            style={{ left: layout.bubbleLeft, width: anchor.width }}
            initial={{ top: anchor.y }}
            animate={{ top: layout.bubbleTop }}
            transition={STACK_MOTION}
          >
            <ChatMessageItem
              message={message}
              isOwnMessage={isOwnMessage}
              showAvatar={showAvatar}
              displayName={displayName}
              avatarUrl={avatarUrl}
              showTail={showTail}
              roomName={roomName}
              currentUserId={currentUserId}
              replyPreview={replyPreview}
              reactions={reactions}
              viewerReaction={viewerReaction}
              overlayMode
            />
          </motion.div>
          <AnchoredMenuPanel
            items={menuItems}
            onSelect={handleMenuSelect}
            anchor={layout.menuAnchor}
            visible={visible}
            minWidth={200}
            embedded
            placement="below"
          />
        </>
      ) : null}
      <ChatEmojiPickerSheet
        visible={emojiPickerOpen}
        onClose={() => setEmojiPickerOpen(false)}
        onSelect={handleEmojiSelect}
      />
    </div>,
    document.body,
  )
}
